import { SimpleChanges } from '@angular/core';

import { SELECTED_ITEM_ALL_ID } from '../const/selected-item-all-id.const';
import { PopulationOptions } from '../model/population-options.model';
import {
    currentLookupState,
    currentMainState,
} from '../population-select.state';

const options: Array<(arg: PopulationOptions) => any> = [
    (that) => that.selectableLobIds,
    (that) => that.selectableRegionIds,
    (that) => that.selectableAreaIds,
    (that) => that.selectableSiteIds,
    (that) => that.selectableSuperGroupIds,
    (that) => that.selectablePeerGroupIds,
    (that) => that.selectableRoleIds,
    (that) => that.selectableQueueIds,
    (that) => that.selectablePopulationGroupIds,

    (that) => that.canSelectAllLobs,
    (that) => that.canSelectAllRegions,
    (that) => that.canSelectAllAreas,
    (that) => that.canSelectAllSites,
    (that) => that.canSelectAllSuperGroups,
    (that) => that.canSelectAllPeerGroups,
    (that) => that.canSelectAllRoles,
    (that) => that.canSelectAllQueues,
    (that) => that.canSelectAllPopulationGroups,
];

const pathPath = (path: Array<string | symbol>): ProxyConstructor => {
    return new Proxy(
        {},
        {
            get(_: any, p: string | symbol, __: any): any {
                if (p === 'isProxy') {
                    return true;
                }
                path.push(p);
                return pathPath(path);
            },
        },
    );
};

export function setOptions<T extends PopulationOptions>(that: T): void {
    const state = currentMainState(that);
    const lookupState = currentLookupState();

    state.lobs = lookupState.lobs;
    if (that.selectableLobIds != null) {
        state.lobs = lookupState.lobs?.filter((e) =>
            that.selectableLobIds.includes(e.value),
        );
    }
    if (that.canSelectAllLobs) {
        state.lobs = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.lobs ?? []),
        ];
    }

    state.regions = lookupState.regions;
    if (that.selectableRegionIds != null) {
        state.regions = lookupState.regions?.filter((e) =>
            that.selectableRegionIds.includes(e.value),
        );
    }
    if (that.canSelectAllRegions) {
        state.regions = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.regions ?? []),
        ];
    }

    state.areas = lookupState.areas;
    if (that.selectableAreaIds != null) {
        state.areas = lookupState.areas?.filter((e) =>
            that.selectableAreaIds.includes(e.value),
        );
    }
    if (that.canSelectAllAreas) {
        state.areas = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.areas ?? []),
        ];
    }

    state.sites = lookupState.sites;
    if (that.selectableSiteIds != null) {
        state.sites = lookupState.sites?.filter((e) =>
            that.selectableSiteIds.includes(e.value),
        );
    }
    if (that.canSelectAllSites) {
        state.sites = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.sites ?? []),
        ];
    }

    state.superGroups = lookupState.superGroups;
    if (that.selectableSuperGroupIds != null) {
        state.superGroups = lookupState.superGroups?.filter((e) =>
            that.selectableSuperGroupIds.includes(e.value),
        );
    }
    if (that.canSelectAllSuperGroups) {
        state.superGroups = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.superGroups ?? []),
        ];
    }

    state.peerGroups = lookupState.peerGroups;
    if (that.selectablePeerGroupIds != null) {
        state.peerGroups = lookupState.peerGroups?.filter((e) =>
            that.selectablePeerGroupIds.includes(e.value),
        );
    }
    if (that.canSelectAllPeerGroups) {
        state.peerGroups = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.peerGroups ?? []),
        ];
    }

    state.roles = lookupState.roles;
    if (that.selectableRoleIds != null) {
        state.roles = lookupState.roles?.filter((e) =>
            that.selectableRoleIds.includes(e.value),
        );
    }
    if (that.canSelectAllRoles) {
        state.roles = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.roles ?? []),
        ];
    }

    state.queues = lookupState.queues;
    if (that.selectableQueueIds != null) {
        state.queues = lookupState.queues?.filter((e) =>
            that.selectableQueueIds.includes(e.value),
        );
    }
    if (that.canSelectAllQueues) {
        state.queues = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.queues ?? []),
        ];
    }

    state.populationGroups = lookupState.populationGroups;
    if (that.selectablePeerGroupIds != null) {
        state.peerGroups = lookupState.peerGroups?.filter((e) =>
            that.selectablePeerGroupIds.includes(e.value),
        );
    }
    if (that.canSelectAllPopulationGroups) {
        state.populationGroups = [
            { value: SELECTED_ITEM_ALL_ID, label: 'All' },
            ...(state.populationGroups ?? []),
        ];
    }
}

export function setOptionsWhenChanged<T extends PopulationOptions>(
    changes: SimpleChanges,
    that: T,
): void {
    if (
        Object.keys(changes).some((e) => {
            for (const item of options) {
                const sourcePath = [];
                item(pathPath(sourcePath) as any);
                const sourcePathStr = sourcePath.join('.');
                if (e === sourcePathStr) {
                    return true;
                }
            }
            return false;
        })
    ) {
        setOptions(that);
    }
}
